import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import * as LucideIcons from 'lucide-react';
import { laravelToYup } from '../utils/laravelToYup';
import type { LaravelRules } from '../utils/laravelToYup';

// Import extracted web renderers - basic renderers
import {
    renderTextInput,
    renderCheckbox,
    renderSwitch,
    renderSelect,
    renderSelectWithOptions,
    renderRadio,
    renderTextarea,
    renderTextareaWithCharacterLeft,
} from '@/MyCode/renderers/web';

// Import extracted component renderers
import {
    renderMultiSelect,
    renderSimpleSlider,
    renderDualRangeSlider,
    renderInputPrefixIcon,
    renderInputPostfixIcon,
    renderInputInlineStartEndAddOn,
    renderInputWithStartSelect,
    renderInputWithEndSelect,
    renderNumberWithPlusMinusSides,
    renderNumberWithChevronsUpDown,
    renderNumberInputWithCommas,
    renderNumberInputWithCommasPlusMinus,
    renderNumberInputWithCommasPlusMinusCompact,
    renderNumberInputPercentageValue,
    renderDateInput,
    renderTimeInput,
    renderDateRangePicker,
    renderAutoCompleteURL,
    renderTagSelect,
    renderCheckSelectBox,
    renderClickToEditAdd,
    renderAddAvatarButton,
    renderMultiImageUpload,
    renderReactNode,
    renderCounterRow,
} from '@/MyCode/renderers/web/components';

// Type definitions
export interface ValidationRule {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
}

export interface UIConfig {
    colSpan?: number;
    icon?: string;
    startAddOn?: string; // Text to display at the start (e.g., currency symbol)
    endAddOn?: string;   // Text to display at the end (e.g., currency code)
    startSelectOptions?: OptionItem[]; // Options for start select dropdown
    endSelectOptions?: OptionItem[]; // Options for end select dropdown
    gridCols?: number; // Number of columns for checkSelectBox grid (default: 3)
    multiSelect?: boolean; // Enable multi-selection for checkSelectBox (default: true)
    // ClickToEditAdd specific properties
    displayFields?: string[]; // Fields to display in the data view
    editFields?: FormField[]; // Fields to show in the edit form
    buttonText?: string; // Text for the Edit/Add button (default: "Edit" or "Add")
    showAddButton?: boolean; // Whether to show Add button when no data exists (default: true)
}

export interface OptionItem {
    label?: string;
    value: string;
    icon?: string; // Icon name for checkSelectBox
    title?: string; // Title for checkSelectBox (alternative to label)
    customContent?: React.ReactNode; // Custom HTML/React content for checkSelectBox (overrides default layout)
}

export interface OptionsConfig {
    source: 'static' | 'dynamic';
    items?: OptionItem[];
    endpoint?: string;
}

export interface OptionGroup {
    label: string;
    items: OptionItem[];
}

export interface GroupedOptionsConfig {
    source: 'static' | 'dynamic';
    groups?: OptionGroup[];
    endpoint?: string;
}

export interface VisibleWhenCondition {
    field: string;
    op: '=' | '!=' | '>' | '<' | 'contains';
    value: any;
}

export interface FormField {
    type: string; // Changed to string to allow custom types
    name: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    maxLength?: number;
    minValue?: number | string; // For number inputs (number) or date inputs (string)
    maxValue?: number | string; // For number inputs (number) or date inputs (string)
    step?: number; // Step value for number inputs
    ui?: UIConfig;
    validations?: ValidationRule;
    options?: OptionsConfig;
    visibleWhen?: VisibleWhenCondition[];
    [key: string]: any; // Allow custom properties for custom field types
}

export interface Step {
    id: string;
    title: string;
    description?: string;
    fields: FormField[];
    laravelValidationRules?: Record<string, LaravelRules>; // Laravel validation rules for this step
}

// Animation configuration
export interface AnimationConfig {
    enabled?: boolean; // Toggle all animations (default: true)
    stepTransition?: boolean; // Enable step transition animations (default: true)
    fieldStagger?: boolean; // Enable field stagger animations (default: true)
    checkSelectBoxIntensity?: 'subtle' | 'medium' | 'bold'; // CheckSelectBox animation intensity (default: 'subtle')
    duration?: number; // Animation duration in ms (default: 300)
    staggerDelay?: number; // Delay between staggered items in ms (default: 50)
}

export interface JsonFormStepsProps {
    steps: Step[];
    onSubmit: (data: Record<string, any>) => void;
    onChange?: (data: Record<string, any>) => void; // Called whenever form data changes
    customFieldRenderers?: FieldRendererRegistry; // Allow custom field renderers
    debug?: boolean; // Enable debug mode with visual debugging panel
    debugSingleForm?: boolean; // If true, render all steps as one form with dividers (for debugging/testing)
    progressBarPosition?: 'top' | 'bottom'; // Position of the step progression bar (default: 'top')
    animations?: AnimationConfig; // Animation configuration
    externalErrors?: Record<string, string>; // Optional external errors (e.g., server-side)
    initialValues?: Record<string, any>; // Optional initial values for the form
    noCard?: boolean; // If true, do not wrap the form in a Card
    submitButtonText?: string | React.ReactNode; // Custom submit button text or React node (default: 'Submit')
    // If true, re-apply initialValues when the prop reference changes.
    // Defaults to false to avoid unintended resets when parents create new objects each render.
    resetOnInitialValuesChange?: boolean;
    horizontalLayout?: boolean; // If true, labels and controls appear on the same line
    hideSubmitButton?: boolean; // If true, hide the submit button
    divClassName?: string; // Custom className for the root div container
}

// Field Renderer Context - all props passed to field renderers
export interface FieldRenderContext {
    field: FormField;
    value: any;
    error: string | null;
    hasError: boolean;
    handleChange: (name: string, value: any) => void;
    formData: Record<string, any>;
    renderIcon: (iconName?: string) => React.ReactNode;
    isRequired: boolean; // Whether field is required based on validation rules
    animConfig?: Required<AnimationConfig>; // Animation configuration
    shouldAnimate?: boolean; // Whether animations should be enabled
}

// Field Renderer Function Type
export type FieldRenderer = (context: FieldRenderContext) => React.ReactNode;

// Field Renderer Registry
export type FieldRendererRegistry = Record<string, FieldRenderer>;

// ============================================================================
// Built-in Field Renderers
// ============================================================================

// NOTE: The following 17 renderers have been extracted to @/MyCode/renderers/web
// and are now imported at the top of this file:
//
// Basic renderers (from @/MyCode/renderers/web):
// - renderTextInput (text, email, number)
// - renderCheckbox
// - renderSelect
// - renderSelectWithOptions
// - renderRadio
// - renderTextarea
// - renderTextareaWithCharacterLeft
//
// Component renderers (from @/MyCode/renderers/web/components):
// - renderMultiSelect
// - renderSimpleSlider
// - renderInputPrefixIcon
// - renderInputPostfixIcon
// - renderInputInlineStartEndAddOn
// - renderInputWithStartSelect
// - renderInputWithEndSelect
// - renderNumberWithPlusMinusSides
// - renderDateInput
// - renderTimeInput

// Remaining renderers below still need to be extracted:
// - renderDualRangeSlider
// - renderNumberWithChevronsUpDown
// - renderDateRangePicker
// - renderAddAvatarButton

// NOTE: The following 14 renderers have been extracted to individual component files
// in @/MyCode/renderers/web/components and removed from this file:
// - renderMultiSelect (MultiSelectRenderer.tsx)
// - renderSimpleSlider (SimpleSliderRenderer.tsx)
// - renderInputPrefixIcon (InputPrefixIconRenderer.tsx)
// - renderInputPostfixIcon (InputPostfixIconRenderer.tsx)
// - renderInputInlineStartEndAddOn (InputInlineAddOnRenderer.tsx)
// - renderInputWithStartSelect (InputWithStartSelectRenderer.tsx)
// - renderInputWithEndSelect (InputWithEndSelectRenderer.tsx)
// - renderNumberWithPlusMinusSides (NumberPlusMinusRenderer.tsx)
// - renderDateInput (DateInputRenderer.tsx)
// - renderTimeInput (TimeInputRenderer.tsx)
// - renderAutoCompleteURL (AutoCompleteURLRenderer.tsx)
// - renderTagSelect (TagSelectRenderer.tsx)
// - renderCheckSelectBox (CheckSelectBoxRenderer.tsx)
// - renderClickToEditAdd (ClickToEditAddRenderer.tsx)

// Dual Range Slider Renderer
// moved to '@/MyCode/renderers/web/components/DualRangeSliderRenderer'


// Number Input with Chevrons Up/Down Stacked Vertically
// moved to '@/MyCode/renderers/web/components/NumberChevronsUpDownRenderer'


// Date Range Picker Renderer (Start and End Date)
// moved to '@/MyCode/renderers/web/components/DateRangePickerRenderer'


// Separate component for AddAvatarButton to properly handle hooks
// moved to '@/MyCode/renderers/web/components/AddAvatarButtonRenderer'


// Default Field Renderer Registry

export const defaultFieldRenderers: FieldRendererRegistry = {
    addAvatarButton: renderAddAvatarButton,
    autoCompleteURL: renderAutoCompleteURL,
    checkbox: renderCheckbox,
    checkSelectBox: renderCheckSelectBox,
    clickToEditAdd: renderClickToEditAdd,
    counterRow: renderCounterRow,
    dateInput: renderDateInput,
    dateRangePicker: renderDateRangePicker,
    dualRangeSlider: renderDualRangeSlider,
    email: renderTextInput,
    inputInlineStartEndAddOn: renderInputInlineStartEndAddOn,
    inputPostfixIcon: renderInputPostfixIcon,
    inputPrefixIcon: renderInputPrefixIcon,
    inputWithEndSelect: renderInputWithEndSelect,
    inputWithStartSelect: renderInputWithStartSelect,
    multiImageUpload: renderMultiImageUpload,
    multiselect: renderMultiSelect,
    number: renderTextInput,
    numberWithChevronsUpDown: renderNumberWithChevronsUpDown,
    numberWithPlusMinusSides: renderNumberWithPlusMinusSides,
    numberInputWithCommas: renderNumberInputWithCommas,
    numberInputWithCommasPlusMinus: renderNumberInputWithCommasPlusMinus,
    numberInputWithCommasPlusMinusCompact: renderNumberInputWithCommasPlusMinusCompact,
    numberInputPercentageValue: renderNumberInputPercentageValue,
    radio: renderRadio,
    reactNode: renderReactNode,
    renderCounterRow,
    renderMultiImageUpload,
    select: renderSelect,
    selectWithOptions: renderSelectWithOptions,
    simpleSlider: renderSimpleSlider,
    switch: renderSwitch,
    tagSelect: renderTagSelect,
    text: renderTextInput,
    textarea: renderTextarea,
    textareaWithCharacterLeft: renderTextareaWithCharacterLeft,
    timeInput: renderTimeInput,
};

// Tailwind grid column safelist - prevents purging of dynamically generated classes
// This ensures all col-span-* classes and their responsive variants are included in the build
const GRID_COLS_SAFELIST = [
    'col-span-1', 'col-span-2', 'col-span-3', 'col-span-4',
    'col-span-5', 'col-span-6', 'col-span-7', 'col-span-8',
    'col-span-9', 'col-span-10', 'col-span-11', 'col-span-12',
    // Responsive variants for sm, md, lg, xl, 2xl
    'sm:col-span-1', 'sm:col-span-2', 'sm:col-span-3', 'sm:col-span-4',
    'sm:col-span-5', 'sm:col-span-6', 'sm:col-span-7', 'sm:col-span-8',
    'sm:col-span-9', 'sm:col-span-10', 'sm:col-span-11', 'sm:col-span-12',
    'md:col-span-1', 'md:col-span-2', 'md:col-span-3', 'md:col-span-4',
    'md:col-span-5', 'md:col-span-6', 'md:col-span-7', 'md:col-span-8',
    'md:col-span-9', 'md:col-span-10', 'md:col-span-11', 'md:col-span-12',
    'lg:col-span-1', 'lg:col-span-2', 'lg:col-span-3', 'lg:col-span-4',
    'lg:col-span-5', 'lg:col-span-6', 'lg:col-span-7', 'lg:col-span-8',
    'lg:col-span-9', 'lg:col-span-10', 'lg:col-span-11', 'lg:col-span-12',
    'xl:col-span-1', 'xl:col-span-2', 'xl:col-span-3', 'xl:col-span-4',
    'xl:col-span-5', 'xl:col-span-6', 'xl:col-span-7', 'xl:col-span-8',
    'xl:col-span-9', 'xl:col-span-10', 'xl:col-span-11', 'xl:col-span-12',
    '2xl:col-span-1', '2xl:col-span-2', '2xl:col-span-3', '2xl:col-span-4',
    '2xl:col-span-5', '2xl:col-span-6', '2xl:col-span-7', '2xl:col-span-8',
    '2xl:col-span-9', '2xl:col-span-10', '2xl:col-span-11', '2xl:col-span-12'
];

const DEBUG_STORAGE_KEY = 'jsonformsteps-debug-enabled';

const JsonFormSteps: React.FC<JsonFormStepsProps> = ({
    steps,
    onSubmit,
    onChange,
    customFieldRenderers,
    debug = false,
    debugSingleForm = false,
    progressBarPosition = 'top',
    animations,
    externalErrors,
    initialValues,
    noCard = false,
    submitButtonText = 'Submit',
    resetOnInitialValuesChange = false,
    horizontalLayout = false,
    hideSubmitButton = false,
    divClassName,
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<Record<string, any>>(() => initialValues || {});
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Apply initial values only on mount by default to avoid resets.
    // If resetOnInitialValuesChange is true, re-apply when the prop reference changes.
    const appliedInitialRef = React.useRef(false)
    const lastInitialRef = React.useRef<Record<string, any> | undefined>(initialValues)
    React.useEffect(() => {
        if (!appliedInitialRef.current) {
            if (initialValues) setFormData(initialValues)
            appliedInitialRef.current = true
            lastInitialRef.current = initialValues
            return
        }
        if (resetOnInitialValuesChange && initialValues !== lastInitialRef.current) {
            setFormData(initialValues || {})
            lastInitialRef.current = initialValues
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues, resetOnInitialValuesChange])

    // Animation configuration with defaults
    const animConfig: Required<AnimationConfig> = {
        enabled: animations?.enabled ?? true,
        stepTransition: animations?.stepTransition ?? true,
        fieldStagger: animations?.fieldStagger ?? true,
        checkSelectBoxIntensity: animations?.checkSelectBoxIntensity ?? 'subtle',
        duration: animations?.duration ?? 300,
        staggerDelay: animations?.staggerDelay ?? 50,
    };

    // Check for reduced motion preference
    const prefersReducedMotion = typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    // Disable animations if user prefers reduced motion or animations are disabled
    const shouldAnimate = animConfig.enabled && !prefersReducedMotion;

    // Debug mode state - persisted in localStorage
    const [debugEnabled, setDebugEnabled] = useState(() => {
        if (!debug) return false;
        const stored = localStorage.getItem(DEBUG_STORAGE_KEY);
        return stored === 'true';
    });

    // Merge default and custom field renderers
    const fieldRenderers: FieldRendererRegistry = {
        ...defaultFieldRenderers,
        ...customFieldRenderers,
    };

    const isSingleForm = steps.length === 1 || debugSingleForm;
    const currentStepData = debugSingleForm ? steps[0] : steps[currentStep];

    // Toggle debug mode and persist to localStorage
    const toggleDebug = (checked: boolean) => {
        setDebugEnabled(checked);
        localStorage.setItem(DEBUG_STORAGE_KEY, String(checked));
    };

    // Check if a field should be visible based on visibleWhen conditions
    const isFieldVisible = (field: FormField): boolean => {
        if (!field.visibleWhen || field.visibleWhen.length === 0) return true;

        const cmp = (a: any, b: any) => a === b || String(a ?? '') === String(b ?? '')

        return field.visibleWhen.every((condition) => {
            const fieldValue = formData[condition.field];
            switch (condition.op) {
                case '=':
                    return cmp(fieldValue, condition.value);
                case '!=':
                    return !cmp(fieldValue, condition.value);
                case '>':
                    return Number(fieldValue) > Number(condition.value);
                case '<':
                    return Number(fieldValue) < Number(condition.value);
                case 'contains':
                    return String(fieldValue ?? '').includes(String(condition.value ?? ''));
                default:
                    return true;
            }
        });
    };

    // Helper to check if a field is required based on Laravel validation rules
    const isFieldRequired = (fieldName: string, stepData?: Step): boolean => {
        const stepToCheck = stepData || currentStepData;
        if (!stepToCheck.laravelValidationRules) return false;
        const rules = stepToCheck.laravelValidationRules[fieldName];
        if (!rules) return false;

        const rulesArray = typeof rules === 'string' ? rules.split('|') : rules;
        return rulesArray.some(rule => rule.toLowerCase() === 'required');
    };

    // Create Yup schema from Laravel validation rules
    // In debugSingleForm mode, combine all step schemas
    const currentStepSchema = useMemo(() => {
        if (debugSingleForm) {
            // Combine validation rules from all steps
            const combinedRules: Record<string, LaravelRules> = {};
            steps.forEach(step => {
                if (step.laravelValidationRules) {
                    Object.assign(combinedRules, step.laravelValidationRules);
                }
            });
            return Object.keys(combinedRules).length > 0 ? laravelToYup(combinedRules) : null;
        }

        if (!currentStepData.laravelValidationRules) {
            return null;
        }
        return laravelToYup(currentStepData.laravelValidationRules);
    }, [currentStepData.laravelValidationRules, debugSingleForm, steps]);

    // Validate current step using Laravel validation rules
    const validateCurrentStep = async (): Promise<boolean> => {
        // If no validation rules, consider valid
        if (!currentStepSchema) {
            setErrors({});
            return true;
        }

        try {
            // Get all visible field names (from all steps if debugSingleForm)
            const visibleFieldNames = debugSingleForm
                ? steps.flatMap(step => step.fields.filter(isFieldVisible).map(f => f.name))
                : currentStepData.fields.filter(isFieldVisible).map(f => f.name);

            // Filter formData to only include visible fields from current step
            const dataToValidate: Record<string, any> = {};
            visibleFieldNames.forEach(name => {
                dataToValidate[name] = formData[name];
            });

            // Validate with Yup schema
            await currentStepSchema.validate(dataToValidate, { abortEarly: false });
            setErrors({});
            return true;
        } catch (err: any) {
            // Yup validation error
            if (err.inner) {
                const newErrors: Record<string, string> = {};
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }
                });
                setErrors(newErrors);
            }
            return false;
        }
    };

    // Handle input change with real-time validation
    const handleChange = (name: string, value: any) => {
        const updatedData = { ...formData, [name]: value };
        setFormData(updatedData);

        // Call onChange callback if provided
        if (onChange) {
            onChange(updatedData);
        }

        // Helper using latest values for visibility checks during live validation
        const isFieldVisibleWith = (data: Record<string, any>, field: FormField): boolean => {
            if (!field.visibleWhen || field.visibleWhen.length === 0) return true;
            return field.visibleWhen.every((condition) => {
                const fieldValue = data[condition.field];
                switch (condition.op) {
                    case '=':
                        return fieldValue === condition.value;
                    case '!=':
                        return fieldValue !== condition.value;
                    case '>':
                        return Number(fieldValue) > Number(condition.value);
                    case '<':
                        return Number(fieldValue) < Number(condition.value);
                    case 'contains':
                        return String(fieldValue ?? '').includes(String(condition.value ?? ''));
                    default:
                        return true;
                }
            });
        };

        // Trigger real-time validation after updating form data
        if (currentStepSchema) {
            // Get visible field names using updated snapshot (from all steps if debugSingleForm)
            const visibleFieldNames = debugSingleForm
                ? steps.flatMap(step => step.fields.filter(f => isFieldVisibleWith(updatedData, f)).map(f => f.name))
                : currentStepData.fields.filter(f => isFieldVisibleWith(updatedData, f)).map(f => f.name);

            // Filter data to only include visible fields
            const dataToValidate: Record<string, any> = {};
            visibleFieldNames.forEach(fieldName => {
                dataToValidate[fieldName] = updatedData[fieldName];
            });

            // Validate asynchronously
            currentStepSchema
                .validate(dataToValidate, { abortEarly: false })
                .then(() => {
                    setErrors({});
                })
                .catch((err: any) => {
                    if (err.inner) {
                        const newErrors: Record<string, string> = {};
                        err.inner.forEach((error: any) => {
                            if (error.path) {
                                newErrors[error.path] = error.message;
                            }
                        });
                        setErrors(newErrors);
                    }
                });
        }
    };

    // Handle next step
    const handleNext = async () => {
        const isValid = await validateCurrentStep();
        if (isValid) {
            setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
        }
    };

    // Handle previous step
    const handlePrevious = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const isValid = await validateCurrentStep();
        if (isValid) {
            onSubmit(formData);
        }
    };

    // Render icon if specified
    const renderIcon = (iconName?: string) => {
        if (!iconName) return null;
        const Icon = (LucideIcons as any)[iconName];
        if (!Icon) return null;
        return <Icon className="w-4 h-4 mr-2 text-muted-foreground" />;
    };

    // Render field based on type using the registry
    const renderField = (field: FormField, stepData?: Step) => {
        const isVisibleNow = isFieldVisible(field);

        const colSpan = field.ui?.colSpan || 12;
        const mergedErrors = { ...(errors || {}), ...(externalErrors || {}) } as Record<string, string>;
        const hasError = !!mergedErrors[field.name];
        const errorMessage = mergedErrors[field.name];

        // Map colSpan to actual Tailwind class from safelist
        // On xs screens (< 640px), always use full width (col-span-12)
        // On sm, md, lg, xl, 2xl screens (>= 640px), honor the specified colSpan
        const getColSpanClass = (span: number): string => {
            const baseClass = GRID_COLS_SAFELIST[span - 1] || 'col-span-12';
            // Always start with col-span-12 for xs (mobile), then override with specified span from sm and up
            // sm: applies to sm, md, lg, xl, 2xl breakpoints
            return `col-span-12 sm:${baseClass}`;
        };

        const fieldWrapper = (content: React.ReactNode) => {
            // On xs: normal layout (label top, control bottom)
            // On sm and up: apply horizontal layout if enabled
            const horizontalLayoutClasses = horizontalLayout
                ? ' sm:[&>div]:flex sm:[&>div]:items-center sm:[&>div]:gap-4 sm:[&>div>label]:w-32 sm:[&>div>label]:shrink-0 sm:[&>div>label]:mb-0 sm:[&_.space-y-2]:flex sm:[&_.space-y-2]:items-center sm:[&_.space-y-2]:gap-4 sm:[&_.space-y-2]:space-y-0 sm:[&_.space-y-2>label]:w-32 sm:[&_.space-y-2>label]:shrink-0 sm:[&_.space-y-2>label]:mb-0'
                : '';

            return (
                <motion.div
                    key={field.name}
                    className={`${getColSpanClass(colSpan)}${isVisibleNow ? '' : ' hidden'}${horizontalLayoutClasses}`}
                    data-visible={isVisibleNow ? 'true' : 'false'}
                    variants={{
                        hidden: { opacity: 0, y: 20 },
                        show: { opacity: 1, y: 0 }
                    }}
                    transition={{
                        duration: animConfig.duration / 1000,
                        ease: "easeOut"
                    }}
                >
                    {content}
                    {hasError && (
                        <p className="text-sm text-destructive mt-1">{errorMessage}</p>
                    )}
                </motion.div>
            );
        };

        // Get the appropriate renderer from the registry
        const renderer = fieldRenderers[field.type];

        if (!renderer) {
            console.warn(`No renderer found for field type: ${field.type}`);
            return null;
        }

        // Create the context object for the renderer
        const context: FieldRenderContext = {
            field,
            value: formData[field.name],
            error: errorMessage,
            hasError,
            handleChange,
            formData,
            renderIcon,
            isRequired: isFieldRequired(field.name, stepData),
            animConfig,
            shouldAnimate,
        };

        // Render the field using the registry
        return fieldWrapper(renderer(context));
    };

    // Render all steps in a single form with dividers
    const renderAllStepsWithDividers = () => {
        return (
            <>
                {steps.map((step, stepIndex) => (
                    <React.Fragment key={step.id}>
                        {/* Step header */}
                        <div className="col-span-12 mb-2">
                            <h3 className="text-lg font-semibold text-primary">{step.title}</h3>
                            {step.description && (
                                <p className="text-sm text-muted-foreground">{step.description}</p>
                            )}
                        </div>

                        {/* Step fields */}
                        {step.fields.map(field => renderField(field, step))}

                        {/* Horizontal divider between steps */}
                        {stepIndex < steps.length - 1 && (
                            <div className="col-span-12 my-6">
                                <hr className="border-t-2 border-border" />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </>
        );
    };

    const isLastStep = currentStep === steps.length - 1;
    const isFirstStep = currentStep === 0;

    // Check if current step has validation errors or empty required fields
    const hasValidationErrors = Object.keys(errors).length > 0;

    // Check if any required fields are empty
    const hasEmptyRequiredFields = useMemo(() => {
        if (debugSingleForm) {
            // Check all steps
            return steps.some(step => {
                if (!step.laravelValidationRules) return false;
                const visibleFieldNames = step.fields
                    .filter(isFieldVisible)
                    .map(f => f.name);
                return visibleFieldNames.some(fieldName => {
                    const isRequired = isFieldRequired(fieldName, step);
                    const value = formData[fieldName];
                    return isRequired && (!value || value === '' || (Array.isArray(value) && value.length === 0));
                });
            });
        }

        if (!currentStepData.laravelValidationRules) return false;

        const visibleFieldNames = currentStepData.fields
            .filter(isFieldVisible)
            .map(f => f.name);

        return visibleFieldNames.some(fieldName => {
            const isRequired = isFieldRequired(fieldName);
            const value = formData[fieldName];
            return isRequired && (!value || value === '' || (Array.isArray(value) && value.length === 0));
        });
    }, [currentStepData, formData, debugSingleForm, steps]);

    // Disable Next/Submit button if there are errors or empty required fields
    const isSubmitDisabled = hasValidationErrors || hasEmptyRequiredFields;

    // Debug panel helper functions
    const getVisibleFields = () => currentStepData.fields.filter(isFieldVisible);
    const getHiddenFields = () => currentStepData.fields.filter(f => !isFieldVisible(f));
    const getAvailableRenderers = () => Object.keys(fieldRenderers);

    // Check for duplicate field names across all steps
    const getDuplicateFieldNames = useMemo(() => {
        const allFieldNames: string[] = [];
        const duplicates: string[] = [];
        const seen = new Set<string>();

        steps.forEach(step => {
            step.fields.forEach(field => {
                if (seen.has(field.name)) {
                    if (!duplicates.includes(field.name)) {
                        duplicates.push(field.name);
                    }
                } else {
                    seen.add(field.name);
                }
                allFieldNames.push(field.name);
            });
        });

        return duplicates;
    }, [steps]);

    // Get cumulative Laravel validation rules from all steps
    const getCumulativeLaravelValidation = useMemo(() => {
        const allRules: Record<string, LaravelRules> = {};
        steps.forEach(step => {
            if (step.laravelValidationRules) {
                Object.assign(allRules, step.laravelValidationRules);
            }
        });
        return allRules;
    }, [steps]);

    // Log warnings for duplicate field names
    React.useEffect(() => {
        if (getDuplicateFieldNames.length > 0) {
            console.warn(
                '⚠️ JsonFormSteps Warning: Duplicate field names detected:',
                getDuplicateFieldNames,
                '\nThis can cause issues with form data and validation. Each field name should be unique across all steps.'
            );
        }
    }, [getDuplicateFieldNames]);

    // Progress bar component
    const progressBar = !isSingleForm && (
        <div className={progressBarPosition === 'top' ? 'mb-6' : 'mt-6'}>
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold">
                    Step {currentStep + 1} of {steps.length}
                </h2>
                <span className="text-sm text-muted-foreground">
                    {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
                </span>
            </div>
            <div className="w-full bg-secondary rounded-full h-2">
                <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                />
            </div>
        </div>
    );

    return (
        <div className={divClassName || "w-full max-w-4xl mx-auto p-0"}>
            {/* Debug Toggle */}
            {debug && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="debug-toggle"
                            checked={debugEnabled}
                            onCheckedChange={toggleDebug}
                        />
                        <Label htmlFor="debug-toggle" className="font-semibold text-yellow-900 dark:text-yellow-200 cursor-pointer">
                            🐛 Debug Mode
                        </Label>
                    </div>
                </div>
            )}

            {/* Debug Panel */}
            {debug && debugEnabled && (
                <Card className="mb-6 border-2 border-yellow-400 dark:border-yellow-600">
                    <CardHeader className="bg-yellow-50 dark:bg-yellow-900/20">
                        <CardTitle className="text-lg flex items-center gap-2">
                            🔍 Debug Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="space-y-4 text-sm">
                            {/* Form State */}
                            <div>
                                <h4 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">📊 Form State</h4>
                                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded font-mono text-xs overflow-auto max-h-48">
                                    <pre>{JSON.stringify(formData, null, 2)}</pre>
                                </div>
                            </div>

                            {/* Current Step Info */}
                            <div>
                                <h4 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">📍 Current Step</h4>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                                        <span className="text-muted-foreground">Step:</span> {currentStep + 1} of {steps.length}
                                    </div>
                                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                                        <span className="text-muted-foreground">Step ID:</span> {currentStepData.id}
                                    </div>
                                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                                        <span className="text-muted-foreground">Mode:</span> {isSingleForm ? 'Single Form' : 'Multi-Step'}
                                    </div>
                                    <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded">
                                        <span className="text-muted-foreground">Total Fields:</span> {currentStepData.fields.length}
                                    </div>
                                </div>
                            </div>

                            {/* Fields Info */}
                            <div>
                                <h4 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">📝 Fields</h4>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded">
                                        <span className="text-muted-foreground">Visible:</span> {getVisibleFields().length}
                                    </div>
                                    <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                        <span className="text-muted-foreground">Hidden:</span> {getHiddenFields().length}
                                    </div>
                                </div>
                                {getHiddenFields().length > 0 && (
                                    <div className="mt-2 bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs">
                                        <span className="text-muted-foreground">Hidden fields:</span>{' '}
                                        {getHiddenFields().map(f => f.name).join(', ')}
                                    </div>
                                )}
                            </div>

                            {/* Duplicate Field Names Warning */}
                            {getDuplicateFieldNames.length > 0 && (
                                <div>
                                    <h4 className="font-semibold mb-2 text-red-700 dark:text-red-400">⚠️ Duplicate Field Names</h4>
                                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded border-2 border-red-300 dark:border-red-700">
                                        <p className="text-xs mb-2 text-red-800 dark:text-red-200">
                                            The following field names appear multiple times across steps. This can cause data conflicts:
                                        </p>
                                        <div className="space-y-1">
                                            {getDuplicateFieldNames.map(name => (
                                                <div key={name} className="text-xs font-semibold text-red-900 dark:text-red-100">
                                                    • {name}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Cumulative Laravel Validation Rules */}
                            {Object.keys(getCumulativeLaravelValidation).length > 0 && (
                                <div>
                                    <h4 className="font-semibold mb-2 text-purple-700 dark:text-purple-400">📋 Cumulative Laravel Validation Rules</h4>
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded font-mono text-xs overflow-auto max-h-64">
                                        <pre>{JSON.stringify(getCumulativeLaravelValidation, null, 2)}</pre>
                                    </div>
                                </div>
                            )}

                            {/* Errors */}
                            {Object.keys(errors).length > 0 && (
                                <div>
                                    <h4 className="font-semibold mb-2 text-red-700 dark:text-red-400">❌ Validation Errors</h4>
                                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded space-y-1">
                                        {Object.entries(errors).map(([field, error]) => (
                                            <div key={field} className="text-xs">
                                                <span className="font-semibold">{field}:</span> {error}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Available Renderers */}
                            {/* <div>
                                <h4 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">🎨 Available Field Renderers</h4>
                                <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded text-xs">
                                    {getAvailableRenderers().map(type => (
                                        <span key={type} className="inline-block bg-blue-100 dark:bg-blue-900/50 px-2 py-1 rounded mr-1 mb-1">
                                            {type}
                                        </span>
                                    ))}
                                </div>
                            </div> */}

                            {/* Field Details */}
                            <div>
                                <h4 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">🔧 Current Step Fields</h4>
                                <div className="space-y-2">
                                    {currentStepData.fields.map(field => (
                                        <div
                                            key={field.name}
                                            className={`p-2 rounded text-xs ${isFieldVisible(field)
                                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                                : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 opacity-60'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <span className="font-semibold">{field.name}</span>
                                                    <span className="text-muted-foreground ml-2">({field.type})</span>
                                                </div>
                                                <div className="flex gap-1">
                                                    {field.required && (
                                                        <span className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-1 rounded text-[10px]">
                                                            required
                                                        </span>
                                                    )}
                                                    {!isFieldVisible(field) && (
                                                        <span className="bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-1 rounded text-[10px]">
                                                            hidden
                                                        </span>
                                                    )}
                                                    {errors[field.name] && (
                                                        <span className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-1 rounded text-[10px]">
                                                            error
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {field.visibleWhen && (
                                                <div className="mt-1 text-[10px] text-muted-foreground">
                                                    Visible when: {JSON.stringify(field.visibleWhen)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Progress bar at top */}
            {progressBarPosition === 'top' && progressBar}

            {noCard ? (
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={shouldAnimate && animConfig.stepTransition ? { opacity: 0, y: 20 } : false}
                        animate={shouldAnimate && animConfig.stepTransition ? { opacity: 1, y: 0 } : {}}
                        exit={shouldAnimate && animConfig.stepTransition ? { opacity: 0, y: -20 } : {}}
                        transition={{ duration: animConfig.duration / 1000, ease: "easeInOut" }}
                    >
                        {/* {(currentStepData.title || currentStepData.description) && (
                            <div className="mb-4">
                                {currentStepData.title && (
                                    <h3 className="text-lg font-semibold">{currentStepData.title}</h3>
                                )}
                                {currentStepData.description && (
                                    <p className="text-sm text-muted-foreground">{currentStepData.description}</p>
                                )}
                            </div>
                        )} */}
                        <form onSubmit={handleSubmit}>
                            <motion.div
                                className="grid grid-cols-12 gap-2"
                                variants={{
                                    hidden: { opacity: 0 },
                                    show: {
                                        opacity: 1,
                                        transition: {
                                            staggerChildren: shouldAnimate && animConfig.fieldStagger
                                                ? animConfig.staggerDelay / 1000
                                                : 0
                                        }
                                    }
                                }}
                                initial="hidden"
                                animate="show"
                            >
                                {debugSingleForm ? renderAllStepsWithDividers() : currentStepData.fields.map(field => renderField(field))}
                            </motion.div>

                            <div className="flex justify-between mt-6">
                                {!isSingleForm && (
                                    <Button type="button" variant="outline" onClick={handlePrevious} disabled={isFirstStep}>
                                        Previous
                                    </Button>
                                )}
                                {isSingleForm && <div />}
                                {isLastStep || isSingleForm ? (
                                    !hideSubmitButton && <Button type="submit" disabled={isSubmitDisabled}>{submitButtonText}</Button>
                                ) : (
                                    <Button type="button" onClick={handleNext} disabled={isSubmitDisabled}>Next</Button>
                                )}
                            </div>
                        </form>
                    </motion.div>
                </AnimatePresence>
            ) : (
                <Card>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={shouldAnimate && animConfig.stepTransition ? { opacity: 0, y: 20 } : false}
                            animate={shouldAnimate && animConfig.stepTransition ? { opacity: 1, y: 0 } : {}}
                            exit={shouldAnimate && animConfig.stepTransition ? { opacity: 0, y: -20 } : {}}
                            transition={{ duration: animConfig.duration / 1000, ease: "easeInOut" }}
                        >
                            {/* <CardHeader>
                                <CardTitle>{currentStepData.title}</CardTitle>
                                {currentStepData.description && (
                                    <CardDescription>{currentStepData.description}</CardDescription>
                                )}
                            </CardHeader> */}
                            <CardContent>
                                <form onSubmit={handleSubmit}>
                                    <motion.div
                                        className="grid grid-cols-12 gap-4"
                                        variants={{
                                            hidden: { opacity: 0 },
                                            show: {
                                                opacity: 1,
                                                transition: {
                                                    staggerChildren: shouldAnimate && animConfig.fieldStagger
                                                        ? animConfig.staggerDelay / 1000
                                                        : 0
                                                }
                                            }
                                        }}
                                        initial="hidden"
                                        animate="show"
                                    >
                                        {debugSingleForm ? renderAllStepsWithDividers() : currentStepData.fields.map(field => renderField(field))}
                                    </motion.div>

                                    <div className="flex justify-between mt-6">
                                        {!isSingleForm && (
                                            <Button type="button" variant="outline" onClick={handlePrevious} disabled={isFirstStep}>
                                                Previous
                                            </Button>
                                        )}
                                        {isSingleForm && <div />}
                                        {isLastStep || isSingleForm ? (
                                            !hideSubmitButton && <Button type="submit" disabled={isSubmitDisabled}>{submitButtonText}</Button>
                                        ) : (
                                            <Button type="button" onClick={handleNext} disabled={isSubmitDisabled}>Next</Button>
                                        )}
                                    </div>
                                </form>
                            </CardContent>
                        </motion.div>
                    </AnimatePresence>
                </Card>
            )}

            {/* Progress bar at bottom */}
            {progressBarPosition === 'bottom' && progressBar}
        </div>
    );
};

export default JsonFormSteps;
